import { layerZAt, layerImageAt } from "./layerHelpers.js";

/** True when a size is locked (has a frozen snapshot). */
export function isSizeLocked(doc, sizeId) {
  return !!doc.lockedSnapshots && sizeId in doc.lockedSnapshots;
}

/**
 * Freeze one size into a self-contained snapshot: only the layers placed on
 * that size, with their per-size box / stacking / image resolved into plain
 * fields, plus its background colour and image. Rendering the snapshot no
 * longer depends on the live doc, so shared edits can't reach a locked banner.
 */
export function buildLockedSnapshot(doc, sizeId) {
  const layers = (doc.layers ?? [])
    .filter((l) => l.boxes[sizeId] && !l.boxes[sizeId].hidden)
    .map((l) => {
      const clone = structuredClone(l);
      clone.z = layerZAt(l, sizeId);
      delete clone.zBySize;
      clone.boxes = { [sizeId]: { ...l.boxes[sizeId] } };
      const img = layerImageAt(l, sizeId);
      clone.imageSource = img ?? undefined;
      delete clone.imageBySize;
      return clone;
    })
    .sort((a, b) => a.z - b.z);
  return {
    layers,
    bgColor: doc.slots.bgColor,
    background: doc.advancedBackground ?? undefined,
  };
}

/**
 * Unlock a size by baking its snapshot back into the live layers as that
 * size's OWN state, so the banner keeps exactly what it was frozen with:
 *  - surviving elements get the snapshot's per-size box / stacking / image,
 *  - elements deleted while locked are resurrected as size-only layers,
 *  - elements added while locked are hidden on this size.
 * The snapshot is then dropped.
 */
export function bakeLockedSnapshot(doc, sizeId) {
  const snap = doc.lockedSnapshots?.[sizeId];
  const snaps = { ...(doc.lockedSnapshots ?? {}) };
  delete snaps[sizeId];
  const lockedSnapshots = Object.keys(snaps).length ? snaps : undefined;
  if (!snap) return { ...doc, lockedSnapshots };

  const snapById = new Map(snap.layers.map((l, i) => [l.id, { l, i }]));
  const layers = (doc.layers ?? []).map((ll) => {
    const hit = snapById.get(ll.id);
    if (!hit) {
      // added after lock → keep it off this frozen size
      return ll.boxes[sizeId]
        ? { ...ll, boxes: { ...ll.boxes, [sizeId]: { ...ll.boxes[sizeId], hidden: true } } }
        : ll;
    }
    const { l: sl, i } = hit;
    const next = {
      ...ll,
      boxes: { ...ll.boxes, [sizeId]: { ...sl.boxes[sizeId], hidden: false } },
      zBySize: { ...(ll.zBySize ?? {}), [sizeId]: i },
    };
    if (ll.type === "image") {
      next.imageBySize = { ...(ll.imageBySize ?? {}), [sizeId]: sl.imageSource ?? null };
    }
    return next;
  });

  // Resurrect elements that were deleted while locked, as size-only layers.
  const liveIds = new Set(layers.map((l) => l.id));
  for (const { l: sl, i } of snapById.values()) {
    if (liveIds.has(sl.id)) continue;
    const revived = structuredClone(sl);
    revived.z = i;
    delete revived.zBySize;
    revived.boxes = { [sizeId]: { ...sl.boxes[sizeId], hidden: false } };
    layers.push(revived);
  }

  return { ...doc, layers, lockedSnapshots };
}